/**
 * IDEAM BART georeference bounds helpers (calibration + Mapbox image sources).
 *
 * Mapbox GL image source coordinates order:
 * top-left, top-right, bottom-right, bottom-left.
 */

export const BOUNDS_LIMITS = {
  lngMin: -180,
  lngMax: 180,
  latMin: -90,
  latMax: 90,
};

export const NUDGE_STEPS = [0.01, 0.05, 0.1];

export function parseBoundsEnv(value) {
  if (!value) {
    return null;
  }

  const parts = String(value)
    .split(",")
    .map((part) => Number(part.trim()))
    .filter((part) => Number.isFinite(part));

  if (parts.length !== 4) {
    return null;
  }

  const [westLng, southLat, eastLng, northLat] = parts;
  return { westLng, southLat, eastLng, northLat };
}

export function validateBounds(bounds) {
  if (!bounds || typeof bounds !== "object") {
    return { valid: false, message: "Bounds are required." };
  }

  const fields = ["westLng", "southLat", "eastLng", "northLat"];
  for (const field of fields) {
    const value = bounds[field];
    if (!Number.isFinite(value)) {
      return { valid: false, message: `${field} must be a number.` };
    }
  }

  const { westLng, southLat, eastLng, northLat } = bounds;

  if (westLng < BOUNDS_LIMITS.lngMin || westLng > BOUNDS_LIMITS.lngMax) {
    return { valid: false, message: "westLng is outside valid longitude range." };
  }
  if (eastLng < BOUNDS_LIMITS.lngMin || eastLng > BOUNDS_LIMITS.lngMax) {
    return { valid: false, message: "eastLng is outside valid longitude range." };
  }
  if (southLat < BOUNDS_LIMITS.latMin || southLat > BOUNDS_LIMITS.latMax) {
    return { valid: false, message: "southLat is outside valid latitude range." };
  }
  if (northLat < BOUNDS_LIMITS.latMin || northLat > BOUNDS_LIMITS.latMax) {
    return { valid: false, message: "northLat is outside valid latitude range." };
  }
  if (westLng >= eastLng) {
    return { valid: false, message: "westLng must be less than eastLng." };
  }
  if (southLat >= northLat) {
    return { valid: false, message: "southLat must be less than northLat." };
  }

  return { valid: true, message: null };
}

/**
 * Mapbox GL image source coordinates:
 * [top-left, top-right, bottom-right, bottom-left]
 */
export function boundsToMapboxCoordinates(bounds) {
  const validation = validateBounds(bounds);
  if (!validation.valid) {
    return null;
  }

  const { westLng, southLat, eastLng, northLat } = bounds;
  return [
    [westLng, northLat],
    [eastLng, northLat],
    [eastLng, southLat],
    [westLng, southLat],
  ];
}

export function mapboxCoordinatesToBounds(coordinates) {
  if (!Array.isArray(coordinates) || coordinates.length !== 4) {
    return null;
  }

  const bounds = {
    westLng: coordinates[0][0],
    northLat: coordinates[0][1],
    eastLng: coordinates[1][0],
    southLat: coordinates[2][1],
  };

  return validateBounds(bounds).valid ? bounds : null;
}

export function boundsToGeoJson(bounds, properties = {}) {
  const validation = validateBounds(bounds);
  if (!validation.valid) {
    return null;
  }

  const { westLng, southLat, eastLng, northLat } = bounds;
  return {
    type: "Feature",
    properties,
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [westLng, northLat],
          [eastLng, northLat],
          [eastLng, southLat],
          [westLng, southLat],
          [westLng, northLat],
        ],
      ],
    },
  };
}

export function nudgeBounds(bounds, direction, step = 0.05) {
  const next = { ...bounds };

  switch (direction) {
    case "west":
      next.westLng -= step;
      next.eastLng -= step;
      break;
    case "east":
      next.westLng += step;
      next.eastLng += step;
      break;
    case "north":
      next.northLat += step;
      next.southLat += step;
      break;
    case "south":
      next.northLat -= step;
      next.southLat -= step;
      break;
    case "expandX":
      next.westLng -= step;
      next.eastLng += step;
      break;
    case "contractX":
      next.westLng += step;
      next.eastLng -= step;
      break;
    case "expandY":
      next.northLat += step;
      next.southLat -= step;
      break;
    case "contractY":
      next.northLat -= step;
      next.southLat += step;
      break;
    default:
      break;
  }

  return validateBounds(next).valid ? next : bounds;
}

export const COLOMBIA_CALIBRATION_REFERENCE_POINTS = [
  {
    id: "bogota",
    name: "Bogotá",
    lng: -74.0721,
    lat: 4.711,
    purpose: "city alignment reference",
  },
  {
    id: "medellin",
    name: "Medellín / Aburrá Valley",
    lng: -75.5636,
    lat: 6.2518,
    purpose: "city alignment reference",
  },
  {
    id: "cali",
    name: "Cali",
    lng: -76.5225,
    lat: 3.4516,
    purpose: "city alignment reference",
  },
  {
    id: "barranquilla",
    name: "Barranquilla / Caribbean coast",
    lng: -74.7813,
    lat: 10.9639,
    purpose: "coastline reference",
  },
  {
    id: "cartagena",
    name: "Cartagena",
    lng: -75.4794,
    lat: 10.391,
    purpose: "coastline reference",
  },
  {
    id: "buenaventura",
    name: "Buenaventura / Pacific coast",
    lng: -77.0197,
    lat: 3.8801,
    purpose: "coastline reference",
  },
  {
    id: "ecuador_border",
    name: "Colombia / Ecuador border",
    lng: -79.0,
    lat: 0.5,
    purpose: "border alignment reference",
  },
  {
    id: "venezuela_border",
    name: "Colombia / Venezuela border",
    lng: -71.5,
    lat: 7.5,
    purpose: "border alignment reference",
  },
];

export function buildReferenceMarkersGeoJson(points = COLOMBIA_CALIBRATION_REFERENCE_POINTS) {
  return {
    type: "FeatureCollection",
    features: points.map((point) => ({
      type: "Feature",
      properties: {
        id: point.id,
        name: point.name,
        purpose: point.purpose,
      },
      geometry: {
        type: "Point",
        coordinates: [point.lng, point.lat],
      },
    })),
  };
}

export function buildCornerLabelGeoJson(bounds) {
  const validation = validateBounds(bounds);
  if (!validation.valid) {
    return { type: "FeatureCollection", features: [] };
  }

  const { westLng, southLat, eastLng, northLat } = bounds;
  const corners = [
    { label: "NW", coordinates: [westLng, northLat] },
    { label: "NE", coordinates: [eastLng, northLat] },
    { label: "SE", coordinates: [eastLng, southLat] },
    { label: "SW", coordinates: [westLng, southLat] },
  ];

  return {
    type: "FeatureCollection",
    features: corners.map((corner) => ({
      type: "Feature",
      properties: { label: corner.label },
      geometry: {
        type: "Point",
        coordinates: corner.coordinates,
      },
    })),
  };
}

export const CALIBRATION_QA_CHECKLIST = [
  "Caribbean coast",
  "Pacific coast",
  "Bogotá",
  "Medellín / Aburrá Valley",
  "Cali",
  "Ecuador border region",
  "Venezuela border region",
  "Radar footprint edges",
];
